/**
 * Microphone in, interviewer's voice out - the two audio formats the Live API
 * speaks.
 *
 * - **In:** raw 16-bit PCM, mono, 16 kHz. Microphones run at 44.1 or 48 kHz, so
 *   a small AudioWorklet averages the samples down to 16 kHz in the audio
 *   thread and posts 100 ms chunks. Averaging in the worklet, rather than
 *   opening the AudioContext at 16 kHz, because some browsers refuse to
 *   connect a 48 kHz microphone to a 16 kHz context.
 * - **Out:** raw 16-bit PCM, mono, 24 kHz, in chunks as it is generated. Each
 *   chunk is scheduled to start exactly when the previous one ends, so the
 *   voice plays as one stream instead of stuttering at every chunk boundary.
 *
 * Both contexts must be created inside the click that starts the turn:
 * browsers only let a page make sound in response to a user gesture, and the
 * gesture expires if the page first waits for the network.
 */

const IN_RATE = 16000
const OUT_RATE = 24000
const CHUNK_SAMPLES = 1600 // 100 ms at 16 kHz

const CAPTURE_WORKLET = `
class PcmCapture extends AudioWorkletProcessor {
  constructor() {
    super()
    this.ratio = sampleRate / ${IN_RATE}
    this.step = 0
    this.sum = 0
    this.count = 0
    this.out = new Int16Array(${CHUNK_SAMPLES})
    this.filled = 0
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0]
    if (!channel) return true
    for (let i = 0; i < channel.length; i++) {
      this.sum += channel[i]
      this.count += 1
      this.step += 1
      if (this.step >= this.ratio) {
        this.step -= this.ratio
        const sample = Math.max(-1, Math.min(1, this.sum / this.count))
        this.sum = 0
        this.count = 0
        this.out[this.filled++] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
        if (this.filled === this.out.length) {
          this.port.postMessage(this.out.buffer.slice(0))
          this.filled = 0
        }
      }
    }
    return true
  }
}
registerProcessor("pcm-capture", PcmCapture)
`

/** Base64 of raw bytes, in slices so a long buffer cannot overflow the call stack. */
export function toBase64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes)
  let binary = ""
  for (let start = 0; start < view.length; start += 0x8000) {
    binary += String.fromCharCode(...view.subarray(start, start + 0x8000))
  }
  return btoa(binary)
}

function fromBase64(data: string): Int16Array {
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2))
}

/** The interviewer's voice: 24 kHz chunks played back to back. */
export class VoicePlayer {
  private readonly context: AudioContext
  private endsAt = 0
  private readonly sources = new Set<AudioBufferSourceNode>()

  constructor() {
    this.context = new AudioContext()
  }

  /** Call in the click handler, before anything is awaited. */
  async unlock(): Promise<void> {
    if (this.context.state === "suspended") await this.context.resume()
  }

  play(base64Pcm: string): void {
    const samples = fromBase64(base64Pcm)
    if (samples.length === 0) return
    const buffer = this.context.createBuffer(1, samples.length, OUT_RATE)
    const channel = buffer.getChannelData(0)
    for (let i = 0; i < samples.length; i++) channel[i] = samples[i] / 0x8000
    const source = this.context.createBufferSource()
    source.buffer = buffer
    source.connect(this.context.destination)
    const startAt = Math.max(this.context.currentTime, this.endsAt)
    source.start(startAt)
    this.endsAt = startAt + buffer.duration
    this.sources.add(source)
    source.onended = () => this.sources.delete(source)
  }

  /** Milliseconds until everything scheduled so far has been heard. */
  remainingMs(): number {
    return Math.max(0, (this.endsAt - this.context.currentTime) * 1000)
  }

  stop(): void {
    for (const source of this.sources) source.stop()
    this.sources.clear()
    this.endsAt = 0
  }

  async close(): Promise<void> {
    this.stop()
    if (this.context.state !== "closed") await this.context.close()
  }
}

/** The candidate's microphone, as 100 ms chunks of 16 kHz PCM. */
export class Microphone {
  private readonly context: AudioContext
  private stream: MediaStream | null = null
  private node: AudioWorkletNode | null = null
  private listener: ((chunk: ArrayBuffer) => void) | null = null

  constructor() {
    this.context = new AudioContext()
  }

  /**
   * Ask for the microphone and start capturing. Chunks are dropped until
   * `send` names a listener, so permission can be asked for while the
   * question is still being read, without anything leaving the page.
   */
  async open(): Promise<void> {
    if (this.context.state === "suspended") await this.context.resume()
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    })
    const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET], { type: "text/javascript" }))
    try {
      await this.context.audioWorklet.addModule(url)
    } finally {
      URL.revokeObjectURL(url)
    }
    const source = this.context.createMediaStreamSource(this.stream)
    this.node = new AudioWorkletNode(this.context, "pcm-capture")
    this.node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => this.listener?.(event.data)
    source.connect(this.node)
  }

  send(listener: ((chunk: ArrayBuffer) => void) | null): void {
    this.listener = listener
  }

  async close(): Promise<void> {
    this.listener = null
    this.node?.disconnect()
    for (const track of this.stream?.getTracks() ?? []) track.stop()
    this.stream = null
    if (this.context.state !== "closed") await this.context.close()
  }
}
